import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeartbeat, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { CRM_NAV, BAND_COLORS } from './nav';

const RISK_COLORS: Record<string, string> = { low: 'success', medium: 'warning', high: 'danger' };

const HealthScores: React.FC = () => {
    const { api } = useInternalApi();
    const [accounts, setAccounts] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [rubric, setRubric] = useState<any[]>([]);
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Churn & Health Scores - Uzima Internal'; }, []);
    useEffect(() => {
        api.get('crm-api/health-scores/').then(r => {
            if (r.data?.status === 'success') {
                setAccounts(r.data.data.accounts || []);
                setSummary(r.data.data.summary);
                setRubric(r.data.data.rubric || []);
                setNote(r.data.data.note || '');
            }
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InternalShell activeDashboard="customer_relations" subNav={CRM_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faHeartbeat} className="me-2 text-primary" />Churn & Health Scores</h2>
            <p className="text-muted mb-4">Practitioner accounts ranked by engagement, lowest first</p>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    {summary && (
                        <div className="row mb-4">
                            <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Healthy</div><h4 className="text-success">{summary.healthy}</h4>
                            </div></div></div>
                            <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">Watch</div><h4 className="text-warning">{summary.watch}</h4>
                            </div></div></div>
                            <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                                <div className="text-muted small mb-1">At risk</div><h4 className="text-danger">{summary.at_risk}</h4>
                            </div></div></div>
                        </div>
                    )}

                    <div className="alert alert-secondary small"><FontAwesomeIcon icon={faInfoCircle} className="me-2" />{note}</div>

                    <div className="row">
                        <div className="col-lg-8 mb-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body p-0">
                                    {accounts.length === 0 ? (
                                        <div className="text-center py-5 text-muted">No practitioner accounts to score yet.</div>
                                    ) : (
                                        <table className="table table-hover align-middle mb-0">
                                            <thead className="table-light"><tr><th>Practitioner</th><th>Score</th><th>Band</th><th>Roster</th><th>Records (30d)</th><th>Churn risk</th></tr></thead>
                                            <tbody>
                                                {accounts.map(a => (
                                                    <tr key={a.principal_id} className={a.band === 'at_risk' ? 'table-danger' : ''}>
                                                        <td><strong>{a.name}</strong><div className="small text-muted">{a.specialization || '—'}</div></td>
                                                        <td style={{ minWidth: 120 }}>
                                                            <div className="progress" style={{ height: 6 }}>
                                                                <div className={`progress-bar bg-${BAND_COLORS[a.band]}`} style={{ width: `${a.score}%` }} />
                                                            </div>
                                                            <span className="small text-muted">{a.score}/100</span>
                                                        </td>
                                                        <td><span className={`badge bg-${BAND_COLORS[a.band]}`}>{a.band.replace('_', ' ')}</span></td>
                                                        <td>{a.roster}</td>
                                                        <td>{a.records_last_30d}</td>
                                                        <td><span className={`badge bg-${RISK_COLORS[a.churn_risk]}`}>{a.churn_risk}</span></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 mb-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-header bg-white"><strong>How the score is built</strong></div>
                                <div className="card-body p-0">
                                    <table className="table table-sm mb-0">
                                        <tbody>
                                            {rubric.map((r, i) => (
                                                <tr key={i}><td className="small">{r.signal}</td><td className="text-end"><span className="badge bg-light text-dark">+{r.points}</span></td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default HealthScores;
